import { supabase } from './supabaseClient';
import type { PaperBlueprint } from '../config/regionConfig';
import type { Provenance } from '../utils/provenanceLabel';

// ── Types ──────────────────────────────────────────────────────

export type QuestionType = 'mcq_single' | 'mcq_multi' | 'numerical' | 'assertion_reasoning' | 'short_answer' | 'long_answer' | 'case_based';

/** One subject's practice set within a multi-subject continuous-flow exam (SAT, JEE Main/Advanced, NEET). */
export interface PracticeSegment {
  subject: string;
  setId: number;
  blueprint: PaperBlueprint;
}

/** Result of one subject's segment within a completed continuous-flow session. */
export interface CompletedSegment {
  subject: string;
  attemptId: number | null;
  score: number;
  total: number;
  marksObtained: number;
  maxMarks: number;
}

export interface PracticeTestSet {
  set_id: number;
  set_name: string;
  question_count: number;
  paper_blueprint?: PaperBlueprint;
  total_marks?: number;
  duration_minutes?: number;
}

export interface PracticeTestQuestion {
  question_id: number;
  order_index: number;
  question_type: QuestionType;
  question: string;
  option_a: string | null;
  option_b: string | null;
  option_c: string | null;
  option_d: string | null;
  correct_option_index: number | null;
  correct_options: number[] | null;
  correct_answer_numerical: number | null;
  numerical_tolerance: number | null;
  model_answer: string | null;
  assertion_text: string | null;
  reason_text: string | null;
  case_study_text: string | null;
  marks: number;
  negative_marks: number;
  section_label: string | null;
  skill_id: string | null;
  /** Phase 7/8: past-paper provenance. Never render "past exam question" copy for anything but 'genuine_past_paper'. */
  provenance: Provenance;
  source_year: number | null;
  source_session: string | null;
  source_question_number: string | null;
}

// Coarse, UI-safe status — the internal 4-state grading outcome
// (understood/partially_understood/needs_reinforcement/misconception) lives
// only inside explain-answer/index.ts and student_practice_attempts.
// understanding_summary; this is what's translated for display and storage.
export type UiUnderstandingStatus = 'solid' | 'mostly_there' | 'needs_review';

export interface QuestionAnswer {
  question_id: number;
  question_type: QuestionType;
  selected?: number | null;
  selected_options?: number[];
  numerical_answer?: number | null;
  text_answer?: string;
  is_correct?: boolean | null;
  marks_awarded: number;
  time_spent_ms: number;
  // Populated after the results page auto-fetches each question's
  // explanation (ChinniExplainer / explain-answer), then persisted via
  // recordPracticeAnswerInsights so the parent overview can read an
  // attempt-level rollup. Absent until then.
  steps?: string[];
  common_mistake?: string;
  ui_status?: UiUnderstandingStatus;
  ui_message?: string;
  detailed_model_answer?: string;
}

/**
 * Per-attempt counts by UI-safe status — explain-answer/index.ts never
 * returns the internal 4-state grading outcome to the client, so this is
 * the finest granularity available for the parent-facing rollup (see
 * get_parent_child_academic_overview's exam_simulator_summary).
 */
export interface UnderstandingSummary {
  solid: number;
  mostly_there: number;
  needs_review: number;
  ungraded: number;
}

export interface PracticeAttempt {
  id: number;
  student_id: string;
  set_id: number;
  score: number;
  total_questions: number;
  answers: QuestionAnswer[];
  started_at: string;
  completed_at: string | null;
  time_taken_seconds: number | null;
  marks_obtained: number | null;
  max_marks: number | null;
  section_scores: Record<string, { obtained: number; max: number }> | null;
}

// ── Service functions ──────────────────────────────────────────

export async function getNextPracticeSet(
  studentId: string,
  examType: string,
  yearLevel: number,
  region?: string,
  subject?: string
): Promise<PracticeTestSet | null> {
  const { data, error } = await supabase.rpc('get_next_practice_set', {
    p_student_id: studentId,
    p_exam_type: examType,
    p_year_level: yearLevel,
    p_region: region || null,
    p_subject: subject || null,
  });

  if (error) {
    console.error('Error getting next practice set:', error);
    return null;
  }

  if (!data || data.length === 0) return null;
  return data[0];
}

export interface PracticeAttemptDetail {
  set_id: number;
  score: number;
  total_questions: number;
  answers: QuestionAnswer[];
  time_taken_seconds: number | null;
  marks_obtained: number | null;
  max_marks: number | null;
  section_scores: Record<string, { obtained: number; max: number }> | null;
  ai_feedback: object | null;
  is_retake: boolean;
}

export async function getPracticeAttempt(
  attemptId: number,
  studentId: string
): Promise<PracticeAttemptDetail | null> {
  const { data, error } = await supabase.rpc('get_practice_attempt', {
    p_attempt_id: attemptId,
    p_student_id: studentId,
  });

  if (error) {
    console.error('Error getting practice attempt:', error);
    return null;
  }

  if (!data || data.length === 0) return null;
  return data[0];
}

export async function getPracticeSetQuestions(
  setId: number
): Promise<PracticeTestQuestion[]> {
  const { data, error } = await supabase.rpc('get_practice_set_questions', {
    p_set_id: setId,
  });

  if (error) {
    console.error('Error getting practice set questions:', error);
    return [];
  }

  return (data || []).map((q: PracticeTestQuestion) => ({
    ...q,
    question_type: q.question_type || 'mcq_single',
    marks: q.marks || 1,
    negative_marks: q.negative_marks || 0,
  }));
}

export async function submitPracticeAttempt(
  studentId: string,
  setId: number,
  score: number,
  total: number,
  answers: QuestionAnswer[],
  timeTaken: number,
  marksObtained?: number,
  maxMarks?: number,
  sectionScores?: Record<string, { obtained: number; max: number }>,
  isRetake?: boolean,
  sessionId?: string
): Promise<number | null> {
  const { data, error } = await supabase.rpc('submit_practice_attempt', {
    p_student_id: studentId,
    p_set_id: setId,
    p_score: score,
    p_total: total,
    p_answers: answers,
    p_time_taken: timeTaken,
    p_marks_obtained: marksObtained || 0,
    p_max_marks: maxMarks || 0,
    p_section_scores: sectionScores || null,
    p_is_retake: isRetake ?? false,
    p_session_id: sessionId || null,
  });

  if (error) {
    console.error('Error submitting practice attempt:', error);
    return null;
  }

  return data;
}

/**
 * Persists per-question explanation/understanding data (fetched via
 * ChinniExplainer/explain-answer on the results page) back onto the attempt,
 * plus the attempt-level understanding_summary rollup the parent overview
 * reads (see get_parent_child_academic_overview's exam_simulator_summary).
 * Fire-and-forget from the caller's perspective — a failure here shouldn't
 * block the student from seeing their own results, which already rendered
 * from the in-memory explain-answer responses regardless.
 */
export async function recordPracticeAnswerInsights(
  attemptId: number,
  studentId: string,
  answers: QuestionAnswer[],
  understandingSummary: UnderstandingSummary
): Promise<boolean> {
  const { error } = await supabase.rpc('record_practice_answer_insights', {
    p_attempt_id: attemptId,
    p_student_id: studentId,
    p_answers: answers,
    p_understanding_summary: understandingSummary,
  });

  if (error) {
    console.error('Error recording practice answer insights:', error);
    return false;
  }
  return true;
}

/** Papers completed so far for one exam+subject (board exams: each subject has its own 20-paper cap). */
export async function getCompletedPaperCount(
  studentId: string,
  examType: string,
  subject: string
): Promise<number> {
  const { data, error } = await supabase.rpc('count_completed_papers', {
    p_student_id: studentId,
    p_exam_type: examType,
    p_subject: subject,
  });

  if (error) {
    console.error('Error counting completed papers:', error);
    return 0;
  }
  return data ?? 0;
}

/** Full exam simulations completed so far (continuous-flow exams: SAT/JEE/NEET share one 20-sitting cap). */
export async function getCompletedSessionCount(
  studentId: string,
  examType: string,
  subjectCount: number
): Promise<number> {
  const { data, error } = await supabase.rpc('count_completed_sessions', {
    p_student_id: studentId,
    p_exam_type: examType,
    p_subject_count: subjectCount,
  });

  if (error) {
    console.error('Error counting completed sessions:', error);
    return 0;
  }
  return data ?? 0;
}

export interface GenerateSetResult {
  set_id: number;
  set_name: string;
  question_count: number;
  total_marks?: number;
  /** 'ready' = generation complete; 'generating' = another request is generating, poll set_id */
  status: 'ready' | 'generating';
  error?: string;
  paperLimitReached?: boolean;
}

/** Extracts the edge function's own JSON error body (e.g. the paper-limit message), if present. */
async function readFunctionError(error: unknown): Promise<{ error?: string; paperLimitReached?: boolean } | null> {
  const ctx = (error as { context?: Response })?.context;
  if (!ctx || typeof ctx.clone !== 'function') return null;
  try {
    const body = await ctx.clone().json();
    return { error: body?.error, paperLimitReached: body?.paper_limit_reached === true };
  } catch {
    return null;
  }
}

export async function generatePracticeSet(
  examType: string,
  yearLevel: number,
  subject: string,
  studentId?: string,
  region?: string,
  blueprint?: PaperBlueprint
): Promise<GenerateSetResult | null> {
  const body = {
    exam_type: examType,
    year_level: yearLevel,
    subject,
    student_id: studentId || null,
    region: region || null,
    blueprint: blueprint || null,
  };

  let { data, error } = await supabase.functions.invoke('generate-practice-questions', { body });

  // 503 = stale generating row was just cleared by the edge function; retry once
  if (error && (error as { status?: number }).status === 503) {
    console.log('Stale generating row cleared — retrying...');
    await new Promise((r) => setTimeout(r, 1000));
    ({ data, error } = await supabase.functions.invoke('generate-practice-questions', { body }));
  }

  if (error) {
    console.error('Error generating practice set:', error);
    const details = await readFunctionError(error);
    if (details?.paperLimitReached) {
      return { set_id: 0, set_name: '', question_count: 0, status: 'ready', error: details.error, paperLimitReached: true };
    }
    return null;
  }

  return data as GenerateSetResult;
}

/**
 * Poll a specific set every 5 s until generation_status becomes 'ready' or 'failed'.
 * Returns true if the set became ready, false on failure or timeout (120 s).
 */
export async function pollForPracticeSet(setId: number): Promise<boolean> {
  const MAX_ATTEMPTS = 24; // 24 × 5 s = 120 s
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    await new Promise((r) => setTimeout(r, 5000));
    const { data } = await supabase
      .from('practice_test_sets')
      .select('generation_status')
      .eq('id', setId)
      .maybeSingle();
    if (data?.generation_status === 'ready') return true;
    if (!data || data?.generation_status === 'failed') return false;
  }
  return false;
}

export interface ArchiveEntry {
  attempt_id: number;
  set_id: number;
  set_name: string;
  exam_type: string;
  subject: string | null;
  score: number;
  total_questions: number;
  marks_obtained: number | null;
  max_marks: number | null;
  section_scores: Record<string, { obtained: number; max: number }> | null;
  completed_at: string;
  time_taken_seconds: number | null;
  ai_feedback: object | null;
  is_retake: boolean;
  /** Shared by every subject's row within one continuous-flow sitting (SAT/JEE/NEET); null for board-exam attempts. */
  session_id: string | null;
  /** Phase 7/8: most-common provenance among this set's questions (null for empty/legacy sets). */
  predominant_provenance: Provenance | null;
}

export async function getPracticeArchive(studentId: string): Promise<ArchiveEntry[]> {
  const { data, error } = await supabase.rpc('get_practice_archive', {
    p_student_id: studentId,
  });

  if (error) {
    console.error('Error loading practice archive:', error);
    return [];
  }

  return data || [];
}

export async function countUnseenSets(
  studentId: string,
  examType: string,
  yearLevel: number,
  region?: string,
  subject?: string,
  blueprintHash?: string
): Promise<number> {
  const { data, error } = await supabase.rpc('count_unseen_practice_sets', {
    p_student_id: studentId,
    p_exam_type: examType,
    p_year_level: yearLevel,
    p_region: region ?? null,
    p_subject: subject ?? null,
    p_blueprint_hash: blueprintHash ?? null,
  });

  if (error) return 0;
  return data ?? 0;
}

export async function getPracticeHistory(
  studentId: string,
  limit = 20
): Promise<PracticeAttempt[]> {
  const { data, error } = await supabase
    .from('student_practice_attempts')
    .select('*')
    .eq('student_id', studentId)
    .order('completed_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error getting practice history:', error);
    return [];
  }

  return data || [];
}
